import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import Header from "../components/Header";
import { Sparkles } from "lucide-react";

const ROLES = [
  { v: "founder", l: "Founder" },
  { v: "executive", l: "Executive (C-Suite)" },
  { v: "investor", l: "Investor" },
  { v: "advisor", l: "Advisor" },
  { v: "researcher", l: "Researcher" },
  { v: "organization", l: "Organization" },
];

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [roleType, setRoleType] = useState("founder");
  const [busy, setBusy] = useState(false);
  const { signin, signup } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signin") {
        await signin(email, password);
        toast.success("Welcome back");
      } else {
        await signup({ email, password, full_name: fullName, role_type: roleType });
        toast.success("Account created");
      }
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const useDemo = async () => {
    setBusy(true);
    try {
      await signin("demo@mahakalitribunal.com", "demotribunal");
      toast.success("Welcome to the demo");
      nav("/app");
    } catch { toast.error("Demo unavailable"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-md mx-auto px-4 py-16 md:py-24">
        <div className="label-caps mb-3">{mode === "signin" ? "Welcome back" : "Join the tribunal"}</div>
        <h1 data-testid="auth-title" className="text-3xl sm:text-4xl tracking-tighter font-semibold mb-2">
          {mode === "signin" ? "Sign in" : "Create your account"}
        </h1>
        <p className="text-muted-foreground text-sm mb-6">
          {mode === "signin" ? "Access your dashboard." : "Two minutes to a global leadership profile."}
        </p>

        <Button
          variant="outline" onClick={useDemo} disabled={busy}
          className="w-full rounded-full h-11 mb-6 border-primary/40 text-primary hover:bg-primary/5"
          data-testid="auth-demo"
        >
          <Sparkles className="h-4 w-4 mr-2" /> Try the demo (no sign-up)
        </Button>

        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-6">
          <div className="flex-1 h-px bg-border" />
          <span className="label-caps">or {mode === "signin" ? "sign in" : "sign up"}</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <Card className="p-6 md:p-8">
          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <>
                <div>
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" value={fullName} onChange={(e) => setFullName(e.target.value)}
                    required data-testid="auth-name" placeholder="Jane Doe" className="mt-1.5" />
                </div>
                <div>
                  <Label>I am a</Label>
                  <Select value={roleType} onValueChange={setRoleType}>
                    <SelectTrigger data-testid="auth-role" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r.v} value={r.v} data-testid={`auth-role-${r.v}`}>{r.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} data-testid="auth-email"
                placeholder="you@company.com" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={password}
                onChange={(e) => setPassword(e.target.value)} data-testid="auth-password"
                placeholder="At least 6 characters" className="mt-1.5" />
            </div>
            <Button type="submit" disabled={busy} data-testid="auth-submit"
              className="w-full rounded-full h-11 mt-2">
              {busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "signin" ? (
              <>Don&apos;t have an account?{" "}
                <button type="button" onClick={() => setMode("signup")} data-testid="switch-signup"
                  className="text-foreground hover:text-primary underline underline-offset-4">Create one</button>
              </>
            ) : (
              <>Already have an account?{" "}
                <button type="button" onClick={() => setMode("signin")} data-testid="switch-signin"
                  className="text-foreground hover:text-primary underline underline-offset-4">Sign in</button>
              </>
            )}
          </div>
        </Card>

        <div className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}
